/**
 * @module SharedIndex
 * @package @geenius/i18n
 * @description Exposes the shared Geenius i18n runtime, locale metadata, and
 * translation helpers. The root package is the canonical public entry point;
 * this private workspace barrel feeds the root package and subpath builds.
 */

export type {
  NamespaceLoader,
  TranslationLoader,
  TranslationLoaderCacheStats,
  TranslationLoaderConfig,
} from "./config";
/**
 * Shared configuration builders and translation loaders used by framework
 * bindings and direct package consumers.
 */
export {
  createI18nConfig,
  createNamespaceLoader,
  createTranslationLoader,
  I18nConfigBuilder,
  i18nPresets,
} from "./config";
/**
 * Stable constants re-exported for consumers that need locale or loader
 * defaults without importing the leaf constants module directly.
 */
export {
  DEFAULT_CACHE_DURATION_MS,
  DEFAULT_LOCALE,
  DEFAULT_LOCALE_STORAGE_KEY,
  DEFAULT_NAMESPACE,
  DEFAULT_TRANSLATIONS_BASE_URL,
  RTL_LOCALE_CODES,
  SUPPORTED_LOCALES,
} from "./constants";
export type {
  CreateI18nDiagnosticsOptions,
  I18nDiagnosticContext,
  I18nDiagnosticEvent,
  I18nDiagnosticLevel,
  I18nDiagnosticLogger,
  I18nDiagnosticScope,
  I18nDiagnostics,
  I18nLoggerScopeOptions,
  I18nMissingKeyDiagnosticInput,
} from "./diagnostics";
/**
 * Shared diagnostics helpers backed by the Geenius logger context utilities.
 */
export {
  createI18nDiagnostics,
  createI18nLoggerScope,
  logMissingKeyDiagnostic,
  mergeI18nDiagnosticContext,
  normalizeI18nDiagnosticContext,
} from "./diagnostics";
export type {
  I18nAdapterInfrastructureError,
  I18nIntegrationError,
} from "./errors";
/**
 * Typed shared errors re-exported for consumer-side discrimination and
 * framework-level error handling.
 */
export {
  I18nConfigError,
  I18nError,
  isAdapterInfrastructureError,
  LocaleNotSupportedError,
  TranslationLoadError,
  TranslationNotFoundError,
  toI18nError,
  toI18nIntegrationError,
} from "./errors";
export type { LocaleStorageAdapter } from "./localeStorage";
/**
 * Locale persistence adapter helpers used by browser framework bindings.
 */
export {
  readBrowserPersistedLocale,
  readPersistedLocaleFromStorage,
  writeBrowserPersistedLocale,
  writePersistedLocaleToStorage,
} from "./localeStorage";
/**
 * Public shared types re-exported for framework bindings, admin tooling, and
 * application-level configuration objects.
 */
export type {
  Direction,
  I18nConfig,
  I18nNamespace,
  InterpolationValues,
  Locale,
  LocaleCode,
  LocaleDirection,
  LocaleInfo,
  LocaleStat,
  MissingKey,
  TranslationDict,
  TranslationEntry,
  TranslationKey,
  TranslationKeyPath,
  TypedTranslator,
} from "./types";
export type {
  BulkImportTranslationsInput,
  I18nConfigInput,
  LocaleInput,
  LocaleStatInput,
  MissingKeyInput,
  MissingKeyReportInput,
  TranslationEntryInput,
  TranslationUpsertInput,
} from "./validators";
/**
 * Zod 4 schemas for validating shared configuration, translations, admin
 * inputs, and provider-compatible records.
 */
export {
  boundedTranslationDictSchema,
  bulkImportTranslationsInputSchema,
  directionSchema,
  i18nConfigSchema,
  i18nNamespaceSchema,
  localeSchema,
  localeStatSchema,
  missingKeyContextSchema,
  missingKeyContextValueSchema,
  missingKeyReportInputSchema,
  missingKeySchema,
  safeI18nNamespaceSchema,
  translationDictSchema,
  translationEntrySchema,
  translationKeySchema,
  translationLoaderConfigSchema,
  translationUpsertInputSchema,
} from "./validators";

import {
  DEFAULT_LOCALE,
  DEFAULT_NAMESPACE,
  RTL_LOCALE_CODES,
  SUPPORTED_LOCALES,
} from "./constants";
import {
  I18nConfigError,
  I18nError,
  LocaleNotSupportedError,
  TranslationNotFoundError,
  toI18nError,
} from "./errors";
import type {
  Direction,
  I18nConfig,
  I18nNamespace,
  InterpolationValues,
  Locale,
  LocaleInfo,
  MissingKey,
  TranslationDict,
  TranslationKeyPath,
  TypedTranslator,
} from "./types";
import { i18nConfigSchema } from "./validators";

/**
 * Interpolation parameters accepted by root translation helpers.
 */
export type TranslationParams = InterpolationValues;

/**
 * Callback invoked by the root pure provider when translation lookup misses.
 */
export type I18nMissingKeyHandler = (
  locale: Locale,
  namespace: I18nNamespace,
  key: string,
  context: {
    count: number;
    fallbackLocale?: Locale;
    source: "t";
  },
) => void;

function reportMissingKeyHandlerError(error: unknown): void {
  globalThis.console?.error?.(
    toI18nError(error, "Shared i18n onMissingKey callback failed."),
  );
}

/**
 * Options used to create a root translation function with `useT`.
 */
export interface UseTOptions {
  locale?: Locale;
  namespace?: I18nNamespace;
  fallbackDict?: TranslationDict;
  fallbackLocale?: Locale;
  strict?: boolean;
  onMissingKey?: I18nMissingKeyHandler;
}

/**
 * Translation function returned by `useT` and the root pure provider.
 */
export type TFunction = (key: string, params?: TranslationParams) => string;

/**
 * Props accepted by the framework-agnostic root provider.
 */
export interface I18nProviderProps {
  children?: unknown;
  config?: Partial<I18nConfig>;
  translations?: TranslationDict;
  messages?: TranslationDict;
  fallbackMessages?: TranslationDict;
  locale?: Locale;
  fallbackLocale?: Locale;
  namespace?: I18nNamespace;
  strict?: boolean;
  onLocaleChange?: (locale: Locale) => void;
  onMissingKey?: I18nMissingKeyHandler;
}

/**
 * Snapshot of the root pure provider state.
 */
export interface I18nProviderState {
  locale: Locale;
  namespace: I18nNamespace;
  direction: Direction;
  isRTL: boolean;
  fallbackLocale?: Locale;
  translations: TranslationDict;
  fallbackMessages: TranslationDict;
  missingKeys: MissingKey[];
}

/**
 * Framework-neutral provider object returned from root `I18nProvider`.
 */
export interface I18nProviderInstance {
  getState: () => I18nProviderState;
  setLocale: (locale: Locale) => void;
  setTranslations: (translations: TranslationDict) => void;
  setFallbackMessages: (translations: TranslationDict) => void;
  t: TFunction;
  useT: () => TFunction;
}

/**
 * Locale metadata indexed by locale code for display and layout decisions.
 */
export const LOCALE_INFO: Record<Locale, LocaleInfo> = {
  en: {
    code: "en",
    name: "English",
    nativeName: "English",
    direction: "ltr",
    flag: "🇬🇧",
  },
  fr: {
    code: "fr",
    name: "French",
    nativeName: "Français",
    direction: "ltr",
    flag: "🇫🇷",
  },
  de: {
    code: "de",
    name: "German",
    nativeName: "Deutsch",
    direction: "ltr",
    flag: "🇩🇪",
  },
  es: {
    code: "es",
    name: "Spanish",
    nativeName: "Español",
    direction: "ltr",
    flag: "🇪🇸",
  },
  pt: {
    code: "pt",
    name: "Portuguese",
    nativeName: "Português",
    direction: "ltr",
    flag: "🇵🇹",
  },
  it: {
    code: "it",
    name: "Italian",
    nativeName: "Italiano",
    direction: "ltr",
    flag: "🇮🇹",
  },
  nl: {
    code: "nl",
    name: "Dutch",
    nativeName: "Nederlands",
    direction: "ltr",
    flag: "🇳🇱",
  },
  ru: {
    code: "ru",
    name: "Russian",
    nativeName: "Русский",
    direction: "ltr",
    flag: "🇷🇺",
  },
  "zh-Hans": {
    code: "zh-Hans",
    name: "Chinese (Simplified)",
    nativeName: "简体中文",
    direction: "ltr",
    flag: "🇨🇳",
  },
  "zh-Hant": {
    code: "zh-Hant",
    name: "Chinese (Traditional)",
    nativeName: "繁體中文",
    direction: "ltr",
    flag: "🇹🇼",
  },
  ja: {
    code: "ja",
    name: "Japanese",
    nativeName: "日本語",
    direction: "ltr",
    flag: "🇯🇵",
  },
  ko: {
    code: "ko",
    name: "Korean",
    nativeName: "한국어",
    direction: "ltr",
    flag: "🇰🇷",
  },
  ar: {
    code: "ar",
    name: "Arabic",
    nativeName: "العربية",
    direction: "rtl",
    flag: "🇸🇦",
  },
  he: {
    code: "he",
    name: "Hebrew",
    nativeName: "עברית",
    direction: "rtl",
    flag: "🇮🇱",
  },
  tr: {
    code: "tr",
    name: "Turkish",
    nativeName: "Türkçe",
    direction: "ltr",
    flag: "🇹🇷",
  },
  pl: {
    code: "pl",
    name: "Polish",
    nativeName: "Polski",
    direction: "ltr",
    flag: "🇵🇱",
  },
  sv: {
    code: "sv",
    name: "Swedish",
    nativeName: "Svenska",
    direction: "ltr",
    flag: "🇸🇪",
  },
  da: {
    code: "da",
    name: "Danish",
    nativeName: "Dansk",
    direction: "ltr",
    flag: "🇩🇰",
  },
  fi: {
    code: "fi",
    name: "Finnish",
    nativeName: "Suomi",
    direction: "ltr",
    flag: "🇫🇮",
  },
  nb: {
    code: "nb",
    name: "Norwegian Bokmål",
    nativeName: "Norsk bokmål",
    direction: "ltr",
    flag: "🇳🇴",
  },
  hi: {
    code: "hi",
    name: "Hindi",
    nativeName: "हिन्दी",
    direction: "ltr",
    flag: "🇮🇳",
  },
  id: {
    code: "id",
    name: "Indonesian",
    nativeName: "Bahasa Indonesia",
    direction: "ltr",
    flag: "🇮🇩",
  },
  vi: {
    code: "vi",
    name: "Vietnamese",
    nativeName: "Tiếng Việt",
    direction: "ltr",
    flag: "🇻🇳",
  },
  fa: {
    code: "fa",
    name: "Persian",
    nativeName: "فارسی",
    direction: "rtl",
    flag: "🇮🇷",
  },
  uk: {
    code: "uk",
    name: "Ukrainian",
    nativeName: "Українська",
    direction: "ltr",
    flag: "🇺🇦",
  },
  cs: {
    code: "cs",
    name: "Czech",
    nativeName: "Čeština",
    direction: "ltr",
    flag: "🇨🇿",
  },
  hu: {
    code: "hu",
    name: "Hungarian",
    nativeName: "Magyar",
    direction: "ltr",
    flag: "🇭🇺",
  },
  ro: {
    code: "ro",
    name: "Romanian",
    nativeName: "Română",
    direction: "ltr",
    flag: "🇷🇴",
  },
  el: {
    code: "el",
    name: "Greek",
    nativeName: "Ελληνικά",
    direction: "ltr",
    flag: "🇬🇷",
  },
  th: {
    code: "th",
    name: "Thai",
    nativeName: "ไทย",
    direction: "ltr",
    flag: "🇹🇭",
  },
  bn: {
    code: "bn",
    name: "Bengali",
    nativeName: "বাংলা",
    direction: "ltr",
    flag: "🇧🇩",
  },
  ur: {
    code: "ur",
    name: "Urdu",
    nativeName: "اردو",
    direction: "rtl",
    flag: "🇵🇰",
  },
  ms: {
    code: "ms",
    name: "Malay",
    nativeName: "Bahasa Melayu",
    direction: "ltr",
    flag: "🇲🇾",
  },
  fil: {
    code: "fil",
    name: "Filipino",
    nativeName: "Filipino",
    direction: "ltr",
    flag: "🇵🇭",
  },
};

const SUPPORTED_LOCALE_STRINGS: readonly string[] = SUPPORTED_LOCALES;

/**
 * Mutable array copy of every supported locale used by framework bindings.
 */
export const ALL_LOCALES: Locale[] = [...SUPPORTED_LOCALES];

/**
 * Mutable array copy of locales that render right-to-left.
 */
export const RTL_LOCALES: Locale[] = [...RTL_LOCALE_CODES];

const VARIANT_LOCALES = new Set<Locale>(["zh-Hans", "zh-Hant", "nb", "fil"]);

/**
 * Checks whether an arbitrary string is one of the supported Geenius locales.
 *
 * @param value Locale-like string to validate.
 * @returns `true` when the value is a supported locale code.
 *
 * @example
 * ```ts
 * if (isLocale(input)) {
 *   const label = LOCALE_INFO[input].nativeName
 * }
 * ```
 */
export function isLocale(value: string): value is Locale {
  return SUPPORTED_LOCALE_STRINGS.includes(value);
}

/**
 * Validates a persisted locale against the supported locale list.
 *
 * @param value Persisted storage value to validate.
 * @param supportedLocales Locale list allowed by the current runtime.
 * @returns The validated locale or `null` when the value is unusable.
 */
export function getPersistedLocale(
  value: string | null,
  supportedLocales: readonly Locale[],
): Locale | null {
  if (!value || !isLocale(value) || !supportedLocales.includes(value)) {
    return null;
  }

  return value;
}

/**
 * Reports whether a locale uses a variant suffix that should be surfaced in UI
 * labels, such as `zh-Hans` or `zh-Hant`.
 *
 * @param locale Locale to inspect.
 * @returns `true` when the locale is a variant locale.
 */
export function isVariantLocale(locale: Locale): boolean {
  return VARIANT_LOCALES.has(locale);
}

/**
 * Formats a locale label for display in locale switchers and admin tables.
 *
 * @param locale Locale to convert into a human-facing label.
 * @returns Native locale label, optionally suffixed with the locale code.
 */
export function getLocaleDisplayLabel(locale: Locale): string {
  const info = LOCALE_INFO[locale];
  if (!info) return locale;
  return isVariantLocale(locale)
    ? `${info.nativeName} (${info.code})`
    : info.nativeName;
}

/**
 * Interpolates `{{token}}` placeholders in a translation string.
 *
 * @param template Translation template containing placeholder tokens.
 * @param params Replacement values keyed by placeholder name.
 * @returns The interpolated translation string.
 */
export function interpolate(
  template: string,
  params: Record<string, string | number>,
): string {
  return template.replace(
    /\{\{(\w+)\}\}|\{(\w+)\}/g,
    (_, doubleKey, singleKey) => {
      const key = doubleKey ?? singleKey;
      const fallback = doubleKey ? `{{${key}}}` : `{${key}}`;
      return String(params[key] ?? fallback);
    },
  );
}

/**
 * Resolves a translation key against a flat dot-key or nested translation
 * dictionary.
 *
 * @param key Translation key path to resolve.
 * @param dict Translation dictionary containing the value.
 * @param params Optional interpolation parameters for the resolved string.
 * @returns The localized string, or the original key when no value exists.
 *
 * @example
 * ```ts
 * t("billing.plan.name", dict, { plan: "Pro" })
 * ```
 */
export function t(
  key: string,
  dict: TranslationDict,
  params?: TranslationParams,
): string {
  const direct = dict[key];
  if (typeof direct === "string") {
    return params ? interpolate(direct, params) : direct;
  }

  const parts = key.split(".");
  let current: string | TranslationDict = dict;
  for (const part of parts) {
    if (typeof current !== "object" || current === null) return key;
    const next: string | TranslationDict | undefined = current[part];
    if (next === undefined) return key;
    current = next;
  }
  if (typeof current !== "string") return key;
  return params ? interpolate(current, params) : current;
}

function isTranslationDictValue(
  value: string | TranslationDict | undefined,
): value is TranslationDict {
  return typeof value === "object" && value !== null;
}

function selectLocaleDictionary(
  dict: TranslationDict,
  locale: Locale | undefined,
): TranslationDict {
  if (!locale) {
    return dict;
  }

  const localeBranch = dict[locale];
  return isTranslationDictValue(localeBranch) ? localeBranch : dict;
}

function translateWithFallback(
  key: string,
  dict: TranslationDict,
  params: TranslationParams | undefined,
  options: UseTOptions,
): string {
  const primary = t(key, selectLocaleDictionary(dict, options.locale), params);

  if (primary !== key) {
    return primary;
  }

  if (options.fallbackDict) {
    const fallback = t(
      key,
      selectLocaleDictionary(options.fallbackDict, options.fallbackLocale),
      params,
    );

    if (fallback !== key) {
      return fallback;
    }
  }

  if (options.locale) {
    try {
      options.onMissingKey?.(
        options.locale,
        options.namespace ?? DEFAULT_NAMESPACE,
        key,
        {
          count: 1,
          ...(options.fallbackLocale
            ? { fallbackLocale: options.fallbackLocale }
            : {}),
          source: "t",
        },
      );
    } catch (error) {
      reportMissingKeyHandlerError(error);
    }
  }

  if (options.strict) {
    throw new TranslationNotFoundError(
      key,
      options.locale ?? options.fallbackLocale ?? DEFAULT_LOCALE,
    );
  }

  return key;
}

/**
 * Creates a framework-neutral translation function.
 *
 * This root helper intentionally does not read React, SolidJS, or browser
 * context. Framework variants wrap it with their native provider APIs.
 *
 * @param dict Translation dictionary or locale-indexed dictionary.
 * @param options Locale, namespace, fallback, and missing-key options.
 * @returns A stable translation function.
 *
 * @example
 * ```ts
 * const translate = useT({ common: { hello: "Hello {{name}}" } })
 * translate("common.hello", { name: "Ada" })
 * ```
 */
export function useT(
  dict: TranslationDict = {},
  options: UseTOptions = {},
): TFunction {
  return (key, params) => translateWithFallback(key, dict, params, options);
}

function createMissingKey(
  locale: Locale,
  namespace: I18nNamespace,
  key: string,
  count: number,
): MissingKey {
  return {
    id: `${locale}:${namespace}:${key}`,
    locale,
    namespace,
    key,
    detectedAt: new Date().toISOString(),
    count,
  };
}

function createRootProviderConfig(props: I18nProviderProps): I18nConfig {
  const candidate = {
    defaultLocale:
      props.config?.defaultLocale ?? props.locale ?? DEFAULT_LOCALE,
    supportedLocales: [...SUPPORTED_LOCALES],
    namespace: DEFAULT_NAMESPACE,
    ...props.config,
    ...(props.fallbackLocale ? { fallbackLocale: props.fallbackLocale } : {}),
    ...(props.namespace ? { namespace: props.namespace } : {}),
  };
  const parsed = i18nConfigSchema.safeParse(candidate);

  if (!parsed.success) {
    throw new I18nConfigError("Invalid root i18n provider configuration", {
      issues: parsed.error.issues,
    });
  }

  return parsed.data;
}

function assertProviderLocale(
  locale: Locale,
  supportedLocales: readonly Locale[],
): void {
  if (!supportedLocales.includes(locale)) {
    throw new LocaleNotSupportedError(locale, supportedLocales);
  }
}

/**
 * Creates a framework-neutral root i18n provider object.
 *
 * The root package cannot own React or SolidJS context, so this provider is a
 * pure runtime facade for shared helpers. Framework subpaths expose JSX
 * providers that bind this shared behavior to their native render model.
 *
 * @param props Initial locale, dictionaries, fallback, and callbacks.
 * @returns A mutable shared provider instance.
 */
export function I18nProvider(
  props: I18nProviderProps = {},
): I18nProviderInstance {
  const config = createRootProviderConfig(props);
  let locale = props.locale ?? config.defaultLocale;
  assertProviderLocale(locale, config.supportedLocales);
  const namespace = config.namespace ?? DEFAULT_NAMESPACE;
  const fallbackLocale = config.fallbackLocale;
  let translations = props.translations ?? props.messages ?? {};
  let fallbackMessages = props.fallbackMessages ?? {};
  const missingKeys = new Map<string, MissingKey>();

  const recordMissingKey: I18nMissingKeyHandler = (
    missingLocale,
    missingNamespace,
    key,
    context,
  ) => {
    const id = `${missingLocale}:${missingNamespace}:${key}`;
    const current = missingKeys.get(id);
    const count = (current?.count ?? 0) + context.count;
    const missingKey = createMissingKey(
      missingLocale,
      missingNamespace,
      key,
      count,
    );

    missingKeys.set(id, missingKey);
    try {
      props.onMissingKey?.(missingLocale, missingNamespace, key, {
        ...context,
        count,
      });
    } catch (error) {
      reportMissingKeyHandlerError(error);
    }
  };

  const getTranslator = () =>
    useT(translations, {
      locale,
      namespace,
      fallbackDict: fallbackMessages,
      fallbackLocale,
      strict: props.strict,
      onMissingKey: recordMissingKey,
    });

  const provider: I18nProviderInstance = {
    getState: () => ({
      locale,
      namespace,
      direction: getDirection(locale),
      isRTL: isRTL(locale),
      ...(fallbackLocale ? { fallbackLocale } : {}),
      translations,
      fallbackMessages,
      missingKeys: Array.from(missingKeys.values()),
    }),
    setLocale: (nextLocale) => {
      assertProviderLocale(nextLocale, config.supportedLocales);
      locale = nextLocale;
      props.onLocaleChange?.(nextLocale);
    },
    setTranslations: (nextTranslations) => {
      translations = nextTranslations;
    },
    setFallbackMessages: (nextFallbackMessages) => {
      fallbackMessages = nextFallbackMessages;
    },
    t: (key, params) => getTranslator()(key, params),
    useT: getTranslator,
  };

  return provider;
}

/**
 * Resolves pluralized translation keys using `_zero`, `_one`, and `_other`
 * suffixes before falling back to the base translation key.
 *
 * @param key Base translation key.
 * @param count Quantity used to choose a plural branch.
 * @param dict Translation dictionary containing plural entries.
 * @returns The localized pluralized string.
 */
export function plural(
  key: string,
  count: number,
  dict: TranslationDict,
): string {
  if (count === 0) {
    const zero = t(`${key}_zero`, dict);
    if (zero !== `${key}_zero`) return interpolate(zero, { count });
  }
  if (count === 1) {
    const one = t(`${key}_one`, dict);
    if (one !== `${key}_one`) return interpolate(one, { count });
  }
  const other = t(`${key}_other`, dict);
  if (other !== `${key}_other`) return interpolate(other, { count });
  return t(key, dict, { count });
}

function createFormatError(
  formatter: "date" | "number" | "currency",
  locale: string,
  cause: unknown,
  context: Record<string, unknown>,
): I18nError {
  return new I18nError(
    `Failed to format ${formatter} for locale "${locale}"`,
    "FORMAT_ERROR",
    cause,
    {
      formatter,
      locale,
      ...context,
    },
  );
}

/**
 * Formats a date for a locale using `Intl.DateTimeFormat`.
 *
 * @param date Date object or ISO string to format.
 * @param locale Locale that controls the formatter.
 * @param format Optional `Intl.DateTimeFormat` options.
 * @returns The localized date string.
 */
export function formatDate(
  date: Date | string,
  locale: Locale,
  format?: Intl.DateTimeFormatOptions,
): string {
  const d = typeof date === "string" ? new Date(date) : date;
  try {
    if (Number.isNaN(d.getTime())) {
      throw new RangeError("Invalid date value");
    }
    return new Intl.DateTimeFormat(
      locale,
      format ?? { dateStyle: "medium" },
    ).format(d);
  } catch (error) {
    throw createFormatError("date", locale, error, {
      input: typeof date === "string" ? date : d.toString(),
    });
  }
}

/**
 * Formats a number for a locale using `Intl.NumberFormat`.
 *
 * @param n Number to format.
 * @param locale Locale that controls number formatting.
 * @param opts Optional `Intl.NumberFormat` options.
 * @returns The localized numeric string.
 */
export function formatNumber(
  n: number,
  locale: Locale,
  opts?: Intl.NumberFormatOptions,
): string {
  try {
    if (!Number.isFinite(n)) {
      throw new TypeError("Number value must be finite");
    }
    return new Intl.NumberFormat(locale, opts).format(n);
  } catch (error) {
    throw createFormatError("number", locale, error, { value: n });
  }
}

/**
 * Formats a currency amount for a locale using `Intl.NumberFormat`.
 *
 * @param amount Monetary amount to format.
 * @param currency ISO currency code to render.
 * @param locale Locale that controls currency formatting.
 * @returns The localized currency string.
 */
export function formatCurrency(
  amount: number,
  currency: string,
  locale: Locale,
): string {
  try {
    if (!Number.isFinite(amount)) {
      throw new TypeError("Currency amount must be finite");
    }
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    }).format(amount);
  } catch (error) {
    throw createFormatError("currency", locale, error, {
      amount,
      currency,
    });
  }
}

function assertSupportedLocaleList(
  supportedLocales: readonly Locale[],
): asserts supportedLocales is readonly [Locale, ...Locale[]] {
  if (supportedLocales.length === 0) {
    throw new I18nConfigError(
      "supportedLocales must include at least one locale",
      {
        supportedLocales,
      },
    );
  }
}

function resolveSupportedBrowserLocale(
  candidate: string,
  supportedLocales: readonly Locale[],
): Locale | null {
  const full = candidate.trim();
  if (isLocale(full) && supportedLocales.includes(full)) return full;

  const lower = full.toLowerCase();
  if (lower.startsWith("zh")) {
    const zhLocale =
      lower.includes("hant") ||
      lower.includes("tw") ||
      lower.includes("hk") ||
      lower.includes("mo")
        ? "zh-Hant"
        : "zh-Hans";
    if (supportedLocales.includes(zhLocale)) return zhLocale;
  }

  if (lower.startsWith("nb") || lower.startsWith("no")) {
    if (supportedLocales.includes("nb")) return "nb";
  }

  if (lower.startsWith("tl") && supportedLocales.includes("fil")) {
    return "fil";
  }

  const browserLang = lower.split("-")[0] ?? "";
  if (isLocale(browserLang) && supportedLocales.includes(browserLang)) {
    return browserLang;
  }

  return (
    supportedLocales.find((locale) => locale.startsWith(`${browserLang}-`)) ??
    null
  );
}

/**
 * Detects the best-fit locale from the browser environment and a supported
 * locale list.
 *
 * @param supportedLocales Locale codes allowed by the current runtime.
 * @returns The detected supported locale, or the first supported fallback.
 * @throws {I18nConfigError} When no supported locales are provided.
 */
export function detectLocale(supportedLocales: readonly Locale[]): Locale {
  assertSupportedLocaleList(supportedLocales);

  if (typeof navigator === "undefined") return supportedLocales[0];

  const preferredLocales = [
    ...(Array.isArray(navigator.languages) ? navigator.languages : []),
    ...(typeof navigator.language === "string" ? [navigator.language] : []),
  ].filter((candidate, index, candidates) => {
    return (
      typeof candidate === "string" &&
      candidate.trim().length > 0 &&
      candidates.indexOf(candidate) === index
    );
  });

  for (const candidate of preferredLocales) {
    const detected = resolveSupportedBrowserLocale(candidate, supportedLocales);
    if (detected) return detected;
  }

  return supportedLocales[0];
}

/**
 * Returns the reading direction associated with a locale.
 *
 * @param locale Locale whose direction should be resolved.
 * @returns `ltr` or `rtl` for the given locale.
 */
export function getDirection(locale: Locale): Direction {
  return LOCALE_INFO[locale]?.direction ?? "ltr";
}

/**
 * Reports whether a locale renders right-to-left.
 *
 * @param locale Locale to inspect.
 * @returns `true` when the locale direction is `rtl`.
 */
export function isRTL(locale: Locale): boolean {
  return getDirection(locale) === "rtl";
}

import { createTranslationLoader } from "./config";

const _defaultLoader = createTranslationLoader({
  baseUrl: "/locales",
  cache: true,
});

/**
 * Loads a translation namespace through the default shared namespace loader.
 *
 * @param locale Locale whose namespace should be loaded.
 * @param ns Namespace identifier to fetch.
 * @returns The loaded translation dictionary.
 *
 * @example
 * ```ts
 * const billing = await loadNamespace("en", "billing")
 * ```
 */
export async function loadNamespace(
  locale: Locale,
  ns: I18nNamespace,
): Promise<TranslationDict> {
  return _defaultLoader.load(locale, ns);
}

/**
 * Flattens a nested translation dictionary into a dot-delimited key map.
 *
 * @param dict Nested translation dictionary to flatten.
 * @param prefix Prefix used during recursive flattening.
 * @returns A flat record keyed by translation paths.
 */
export function flattenDict(
  dict: TranslationDict,
  prefix = "",
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(dict)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (typeof value === "string") result[fullKey] = value;
    else Object.assign(result, flattenDict(value, fullKey));
  }
  return result;
}

/**
 * Creates a strongly typed translator object for a known translation
 * dictionary.
 *
 * @param dict Translation dictionary used to derive the typed translator.
 * @returns A typed translator with lookup, existence, and flatten helpers.
 *
 * @example
 * ```ts
 * const translator = createTypedTranslator(messages)
 * translator.t("dashboard.title")
 * ```
 */
export function createTypedTranslator<T extends TranslationDict>(
  dict: T,
): TypedTranslator<T> {
  const flat = flattenDict(dict);

  const has = (key: TranslationKeyPath<T>) => {
    return Object.hasOwn(flat, key);
  };

  const isTypedKey = (key: string): key is TranslationKeyPath<T> => {
    return Object.hasOwn(flat, key);
  };

  return {
    t: (key, params) => t(String(key), dict, params),
    has,
    keys: () => Object.keys(flat).filter(isTypedKey),
    // `flattenDict()` produces the exact keys represented by `TranslationKeyPath<T>`.
    flat: () => flat as Record<TranslationKeyPath<T>, string>,
  };
}
